import { FLIGHT_DEALS_ONLY } from "@/lib/release";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, setActiveOrg, setAuthTokens, setRefreshToken, setToken } from "@/lib/api";
import { createPhoneVerification } from "@/lib/firebasePhone";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function computeHome(perms, roles, accountType) {
  if (accountType === "traveller") return "/traveller";
  const isOperator = perms.includes("trip:edit") || perms.includes("trip:create") || perms.includes("participant:manage");
  if (isOperator) return "/trips";
  return "/my";
}

export function AuthProvider({ children }) {
  const sessionVersion = useRef(0);
  const meRequest = useRef(0);
  const phoneVerification = useRef(null);
  const phoneRequest = useRef(0);
  if (!phoneVerification.current) phoneVerification.current = createPhoneVerification();
  const clearPhoneOtp = useCallback(() => {
    phoneRequest.current += 1;
    phoneVerification.current.clear();
  }, []);
  useEffect(() => clearPhoneOtp, [clearPhoneOtp]);
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [perms, setPerms] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeOrg, setActiveOrgState] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [organizationProfile, setOrganizationProfile] = useState(null);

  const loadOrganizationProfile = useCallback(async (orgId) => {
    const version = sessionVersion.current;
    if (!orgId) {
      setOrganizationProfile(null);
      return null;
    }
    try {
      const { data } = await api.get("/org/profile");
      if (version !== sessionVersion.current) return null;
      const profile = {
        ...(data.profile || {}),
        display_name: data.profile?.display_name || data.organization?.name || "",
      };
      setOrganizationProfile(profile);
      return profile;
    } catch {
      if (version !== sessionVersion.current) return null;
      setOrganizationProfile(null);
      return null;
    }
  }, []);

  const refreshMe = useCallback(async (expectedUserId = null) => {
    const version = sessionVersion.current;
    const request = ++meRequest.current;
    try {
      const { data } = await api.get("/me");
      if (version !== sessionVersion.current || request !== meRequest.current) return null;
      if (expectedUserId && data.user?.id !== expectedUserId) {
        setToken(null); setRefreshToken(null); setActiveOrg(null);
        setUser(false); setPerms([]); setRoles([]); setMemberships([]);
        setActiveOrgState(null); setOrganizationProfile(null);
        throw new Error("The signed-in account did not match verification. Please sign in again.");
      }
      setUser(data.user);
      setPerms(data.effective_permissions || []);
      setMemberships(data.memberships || []);
      const active = data.active_org_id;
      if (active) {
        setActiveOrg(active);
        setActiveOrgState(active);
      } else {
        setActiveOrg(null);
        setActiveOrgState(null);
      }
      const mem = (data.memberships || []).find((m) => m.org_id === active);
      setRoles(mem?.roles || []);
      if (!FLIGHT_DEALS_ONLY) await loadOrganizationProfile(active);
      return data;
    } catch (error) {
      if (version !== sessionVersion.current || request !== meRequest.current) return null;
      setUser(false);
      if (expectedUserId) throw error;
      return null;
    }
  }, [loadOrganizationProfile]);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const applyAuthData = useCallback(async (data) => {
    sessionVersion.current += 1;
    setAuthTokens(data.access_token, data.refresh_token);
    if (data.memberships?.[0]) {
      setActiveOrg(data.memberships[0].org_id);
      setActiveOrgState(data.memberships[0].org_id);
    } else {
      setActiveOrg(null);
      setActiveOrgState(null);
    }
    const profile = await refreshMe(data.user?.id);
    if (!profile) throw new Error("Sign-in could not be completed. Please sign in again.");
    return profile;
  }, [refreshMe]);

  const login = useCallback(async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    return await applyAuthData(data);
  }, [applyAuthData]);

  const register = useCallback(async (fullName, email, password, phone, accountType = "traveller", organizationName = "") => {
    const normalizedAccountType = !FLIGHT_DEALS_ONLY && accountType === "organizer" ? "organizer" : "traveller";
    const { data } = await api.post("/auth/register", {
      full_name: fullName,
      email: email?.trim() || null,
      phone,
      password,
      account_type: normalizedAccountType,
      organization_name: organizationName.trim(),
    });
    if (data.pending_registration && data.phone) {
      clearPhoneOtp();
      const request = phoneRequest.current;
      try {
        const challenge = await api.post("/auth/registration/phone/start", { registration_token: data.registration_token });
        if (request !== phoneRequest.current) throw new Error("Phone verification was cancelled. Request a new code.");
        await phoneVerification.current.send(challenge.data, "signup_phone");
        data.verification_delivery = { ...data.verification_delivery, phone: { sent: true } };
      } catch (error) {
        data.verification_delivery = { ...data.verification_delivery, phone: { sent: false, error: "SMS could not be sent. Request a code below." } };
      }
    }
    return data;
  }, [clearPhoneOtp]);

  const startPhoneOtp = useCallback(async (phone) => {
    clearPhoneOtp();
    const request = phoneRequest.current;
    const { data } = await api.post("/auth/otp/start", { phone });
    if (request !== phoneRequest.current) throw new Error("Phone verification was cancelled. Request a new code.");
    if (data.provider === "firebase") return phoneVerification.current.send(data, "verify_phone");
    return data;
  }, [clearPhoneOtp]);

  const verifyPhoneOtp = useCallback(async (phone, code) => {
    const proof = await phoneVerification.current.confirm(phone, code, "verify_phone");
    const { data } = await api.post("/auth/otp/verify", { phone, ...proof });
    clearPhoneOtp();
    return await applyAuthData(data);
  }, [applyAuthData, clearPhoneOtp]);

  const resendEmailVerification = useCallback(async () => {
    const { data } = await api.post("/auth/email/resend");
    return data;
  }, []);

  const verifyEmail = useCallback(async (token, signup = false) => {
    const { data } = await api.post(signup ? "/auth/registration/email/verify" : "/auth/email/verify", { token });
    if (data.pending_registration || (data.completed && !data.access_token)) return data;
    return await applyAuthData(data);
  }, [applyAuthData]);

  const startRegistrationPhone = useCallback(async (registrationToken) => {
    clearPhoneOtp();
    const request = phoneRequest.current;
    const { data } = await api.post("/auth/registration/phone/start", { registration_token: registrationToken });
    if (request !== phoneRequest.current) throw new Error("Phone verification was cancelled. Request a new code.");
    return phoneVerification.current.send(data, "signup_phone");
  }, [clearPhoneOtp]);

  const verifyRegistrationPhone = useCallback(async (registrationToken, phone, code) => {
    const proof = await phoneVerification.current.confirm(phone, code, "signup_phone");
    const { data } = await api.post("/auth/registration/phone/verify", { registration_token: registrationToken, ...proof });
    clearPhoneOtp();
    if (data.pending_registration || (data.completed && !data.access_token)) return data;
    return await applyAuthData(data);
  }, [applyAuthData, clearPhoneOtp]);

  const requestPasswordRecovery = useCallback(async (identifier) => {
    clearPhoneOtp();
    const request = phoneRequest.current;
    const { data } = await api.post("/auth/recovery/start", { identifier });
    if (request !== phoneRequest.current) throw new Error("Phone verification was cancelled. Request a new code.");
    if (data.provider === "firebase") {
      const delivery = await phoneVerification.current.send(data, "password_recovery");
      return { ...delivery, channels: { phone: true } };
    }
    return data;
  }, [clearPhoneOtp]);

  const verifyRecoveryOtp = useCallback(async (identifier, code) => {
    const proof = await phoneVerification.current.confirm(identifier, code, "password_recovery");
    const { data } = await api.post("/auth/recovery/otp/verify", { identifier, ...proof });
    clearPhoneOtp();
    return data;
  }, [clearPhoneOtp]);

  const verifyRecoveryEmail = useCallback(async (token) => {
    const { data } = await api.post("/auth/recovery/email/verify", { token });
    return data;
  }, []);

  const completePasswordRecovery = useCallback(async (resetToken, password, options = {}) => {
    const { data } = await api.post("/auth/recovery/complete", { reset_token: resetToken, password, ...(options.signIn === false ? { auto_login: false } : {}) });
    if (options.signIn === false) {
      sessionVersion.current += 1;
      clearPhoneOtp(); setToken(null); setRefreshToken(null); setActiveOrg(null);
      setUser(false); setPerms([]); setRoles([]); setMemberships([]);
      setActiveOrgState(null); setOrganizationProfile(null);
      return data;
    }
    return await applyAuthData(data);
  }, [applyAuthData, clearPhoneOtp]);

  const switchOrg = async (orgId) => {
    await api.post("/auth/switch-org", { org_id: orgId });
    setActiveOrg(orgId);
    setActiveOrgState(orgId);
    return await refreshMe();
  };

  const logout = async () => {
    sessionVersion.current += 1;
    const version = sessionVersion.current;
    clearPhoneOtp();
    try { await api.post("/auth/logout"); } catch {}
    if (version !== sessionVersion.current) return;
    setToken(null);
    setRefreshToken(null);
    setUser(false); setPerms([]); setRoles([]); setMemberships([]); setOrganizationProfile(null);
  };

  const home = FLIGHT_DEALS_ONLY ? "/flight-deals" : computeHome(perms, roles, user?.account_type);

  return (
    <AuthContext.Provider value={{ user, perms, roles, activeOrg, memberships, organizationProfile, refreshOrganizationProfile: () => loadOrganizationProfile(activeOrg), home, login, register, startRegistrationPhone, verifyRegistrationPhone, startPhoneOtp, verifyPhoneOtp, clearPhoneOtp, resendEmailVerification, verifyEmail, requestPasswordRecovery, verifyRecoveryOtp, verifyRecoveryEmail, completePasswordRecovery, logout, switchOrg, refreshMe, setToken, setActiveOrg }}>
      {children}
    </AuthContext.Provider>
  );
}
